// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * What comes out of the network, and what the camera is allowed to do with it.
 *
 * Depth Anything V2 emits RELATIVE INVERSE DEPTH — disparity. Larger means
 * closer, there is no metric scale, and two photographs of the same room can
 * come back with completely different numeric ranges. Nothing downstream may
 * assume otherwise, which is why every value in this file is normalised per
 * image before it is used for anything.
 *
 * The important function here is `excursionCap`. It is the whole reason this
 * tool does not look broken.
 *
 * A single displaced sheet cannot invent the pixels that were hidden behind a
 * foreground object, so when the camera moves sideways the triangles spanning a
 * depth edge stretch, and the stretched texels are a smear. That smear is not
 * mysterious: for a forward-warped sheet, the width of the invented region at
 * an edge is exactly
 *
 *     invented_width_px  =  excursion_px  x  depth_jump
 *
 * with depth in the normalised [0,1] units below. This was measured against a
 * 1-D warp over a 30 px disparity jump spread across 1, 2, 4, 8, 16 and 32
 * pixels: the total invented width came out at 6.00 px in EVERY case at an
 * excursion of 0.2, while the per-pixel gradient fell from 30.00 to 0.94. The
 * softness of the edge is irrelevant; only its total height matters.
 *
 * That is why the cap is driven by a windowed JUMP map rather than a gradient.
 * On Middlebury ground-truth disparity, blurring the field to imitate a
 * network's own smoothing collapsed the 99.5th-percentile per-pixel gradient by
 * 5x (11.00 -> 2.17) while the windowed jump moved 15% (18.75 -> 15.88). A
 * gradient-driven cap would therefore hand a smooth depth-anything field about
 * five times too much excursion, with the smear unchanged.
 */

/** A normalised depth field: [0,1], larger = closer. Row-major, w x h. */
export interface DepthField {
  readonly data: Float32Array;
  readonly width: number;
  readonly height: number;
}

export interface NormaliseReport {
  /** The raw values the [0,1] range was pinned to. */
  readonly lo: number;
  readonly hi: number;
  /** Fraction of samples clipped at each end by the robust percentiles. */
  readonly clippedLow: number;
  readonly clippedHigh: number;
}

/**
 * Percentile of an already-sorted ascending array, linearly interpolated.
 *
 * `p` is a fraction in [0,1].
 */
export function percentileSorted(sorted: ArrayLike<number>, p: number): number {
  const n = sorted.length;
  if (n === 0) return Number.NaN;
  if (n === 1) return sorted[0]!;
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
  const pos = clamped * (n - 1);
  const i = Math.floor(pos);
  const frac = pos - i;
  const a = sorted[i]!;
  const b = sorted[Math.min(n - 1, i + 1)]!;
  return a + (b - a) * frac;
}

/**
 * Sort a copy — or a decimated sample of one — for percentile work.
 *
 * A 12 MP field is 12 million floats and sorting it costs more than the depth
 * estimate did. Sampling every k-th value moves a 2nd/98th percentile by far
 * less than the quantisation of the display, so the cap is unaffected; `stride`
 * is chosen so at most `MAX_SAMPLES` values are sorted.
 */
const MAX_SAMPLES = 262_144;

export function sortedSample(data: Float32Array): Float32Array {
  const stride = Math.max(1, Math.ceil(data.length / MAX_SAMPLES));
  const n = Math.ceil(data.length / stride);
  const out = new Float32Array(n);
  for (let i = 0, j = 0; j < n; i += stride, j += 1) out[j] = data[i]!;
  return out.sort();
}

/**
 * Map a raw disparity field to [0,1] using robust percentiles.
 *
 * The percentiles matter. One blown-out sky pixel or one speck of sensor noise
 * at the near end otherwise squashes the entire useful range into a sliver, and
 * every downstream number — the jump map, the cap, the displacement — is
 * computed against a range that a handful of pixels defined.
 */
export function normaliseDepth(
  raw: Float32Array,
  width: number,
  height: number,
  loPct = 0.02,
  hiPct = 0.98,
): { field: DepthField; report: NormaliseReport } {
  if (raw.length !== width * height) {
    throw new Error(`depth field is ${raw.length} values, expected ${width * height}`);
  }
  const sorted = sortedSample(raw);
  let lo = percentileSorted(sorted, loPct);
  let hi = percentileSorted(sorted, hiPct);
  if (!(hi > lo)) {
    // A completely flat field. Everything is at the same distance, the cap will
    // come out unbounded, and the caller has to notice — so say so honestly
    // rather than dividing by zero.
    lo = sorted[0] ?? 0;
    hi = sorted[sorted.length - 1] ?? lo + 1;
  }
  const span = hi - lo || 1;
  const data = new Float32Array(raw.length);
  let clippedLow = 0;
  let clippedHigh = 0;
  for (let i = 0; i < raw.length; i += 1) {
    const v = (raw[i]! - lo) / span;
    if (v < 0) {
      clippedLow += 1;
      data[i] = 0;
    } else if (v > 1) {
      clippedHigh += 1;
      data[i] = 1;
    } else {
      data[i] = v;
    }
  }
  return {
    field: { data, width, height },
    report: {
      lo,
      hi,
      clippedLow: clippedLow / raw.length,
      clippedHigh: clippedHigh / raw.length,
    },
  };
}

/**
 * The OTHER normalisation, for the exported depth map.
 *
 * The robust percentiles above are right for the geometry and wrong for the
 * file. They clip about four per cent of the picture flat against 0 and 1,
 * which is exactly what you want when a single hot pixel would otherwise decide
 * how far the camera may travel — and exactly what you do not want in a depth
 * map somebody is going to load into something else, where those clipped
 * regions become featureless white and black plates.
 *
 * So the PNG gets its own pass, over the true minimum and maximum, and the two
 * normalisations are kept apart on purpose.
 */
export function minMaxNormalise(raw: Float32Array, width: number, height: number): DepthField {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < raw.length; i += 1) {
    const v = raw[i]!;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const span = hi - lo || 1;
  const data = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) data[i] = (raw[i]! - lo) / span;
  return { data, width, height };
}

/**
 * Sliding-window maximum over a 1-D run, via a monotonic deque. O(n).
 *
 * `out[i]` is the maximum of `read(i-radius) .. read(i+radius)`, clamped to the
 * ends of the run. Written against an index accessor so the same routine serves
 * rows and columns without transposing a multi-megapixel buffer.
 */
function windowExtremes(
  length: number,
  radius: number,
  read: (i: number) => number,
  writeMax: (i: number, v: number) => void,
  writeMin: (i: number, v: number) => void,
): void {
  const maxDeque = new Int32Array(length);
  const minDeque = new Int32Array(length);
  let maxHead = 0;
  let maxTail = 0;
  let minHead = 0;
  let minTail = 0;
  let next = 0;

  for (let i = 0; i < length; i += 1) {
    const limit = Math.min(length - 1, i + radius);
    while (next <= limit) {
      const v = read(next);
      while (maxTail > maxHead && read(maxDeque[maxTail - 1]!) <= v) maxTail -= 1;
      maxDeque[maxTail] = next;
      maxTail += 1;
      while (minTail > minHead && read(minDeque[minTail - 1]!) >= v) minTail -= 1;
      minDeque[minTail] = next;
      minTail += 1;
      next += 1;
    }
    const from = i - radius;
    while (maxDeque[maxHead]! < from) maxHead += 1;
    while (minDeque[minHead]! < from) minHead += 1;
    writeMax(i, read(maxDeque[maxHead]!));
    writeMin(i, read(minDeque[minHead]!));
  }
}

/**
 * The windowed jump map.
 *
 * `J(x,y)` is the largest absolute depth difference between this pixel and any
 * pixel within `radius` along either axis. Both axes, because an orbit or a
 * sway moves the camera vertically as well as horizontally and a horizontal-only
 * statistic would under-report a horizon.
 */
export function jumpMap(field: DepthField, radius: number): Float32Array {
  const { data, width, height } = field;
  const r = Math.max(1, Math.round(radius));
  const out = new Float32Array(data.length);
  const rowMax = new Float32Array(Math.max(width, height));
  const rowMin = new Float32Array(Math.max(width, height));

  for (let y = 0; y < height; y += 1) {
    const base = y * width;
    windowExtremes(
      width,
      r,
      (i) => data[base + i]!,
      (i, v) => {
        rowMax[i] = v;
      },
      (i, v) => {
        rowMin[i] = v;
      },
    );
    for (let x = 0; x < width; x += 1) {
      const d = data[base + x]!;
      const j = Math.max(rowMax[x]! - d, d - rowMin[x]!);
      out[base + x] = j;
    }
  }

  for (let x = 0; x < width; x += 1) {
    windowExtremes(
      height,
      r,
      (i) => data[i * width + x]!,
      (i, v) => {
        rowMax[i] = v;
      },
      (i, v) => {
        rowMin[i] = v;
      },
    );
    for (let y = 0; y < height; y += 1) {
      const idx = y * width + x;
      const d = data[idx]!;
      const j = Math.max(rowMax[y]! - d, d - rowMin[y]!);
      if (j > out[idx]!) out[idx] = j;
    }
  }
  return out;
}

/** The window radius the cap is measured over: ~2% of the longer edge. */
export function jumpRadius(width: number, height: number): number {
  return Math.max(2, Math.round(Math.max(width, height) * 0.02));
}

export interface ExcursionCap {
  /** Peak-to-peak camera excursion, in pixels of the RENDERED frame. */
  readonly parallaxPx: number;
  /** The same, as a fraction of the rendered frame's width. */
  readonly fraction: number;
  /** The two statistics it was derived from, so the UI can show its working. */
  readonly j995: number;
  readonly jmax: number;
  /** The smear tolerance, in rendered pixels, that produced this cap. */
  readonly tolerancePx: number;
}

/**
 * How far the camera may move before the invention becomes visible.
 *
 * Two bounds, and the smaller wins:
 *
 *   tol / J99.5   holds the TYPICAL worst edge inside the tolerance;
 *   4 tol / Jmax  stops the single steepest edge in the frame from blowing out
 *                 by more than four times it.
 *
 * Closed on Middlebury ground truth: at tol = 2.5 px this yields 1.51% of frame
 * width on `cones` and 1.42% on `teddy`, with a measured worst smear anywhere in
 * the frame of 3.3 px and 2.7 px and 0.035% / 0.002% of the frame invented
 * beyond tolerance.
 */
export function excursionCap(
  jump: Float32Array,
  renderWidth: number,
  tolerancePx = 2.5,
): ExcursionCap {
  const sorted = sortedSample(jump);
  const j995 = percentileSorted(sorted, 0.995);
  let jmax = 0;
  for (let i = 0; i < jump.length; i += 1) if (jump[i]! > jmax) jmax = jump[i]!;

  // A flat field has no edges to smear, so the limit becomes the frame itself
  // rather than infinity — beyond that the photograph slides off the screen.
  const byTypical = j995 > 1e-6 ? tolerancePx / j995 : renderWidth;
  const byWorst = jmax > 1e-6 ? (4 * tolerancePx) / jmax : renderWidth;
  const parallaxPx = Math.min(byTypical, byWorst, renderWidth * 0.12);
  return {
    parallaxPx,
    fraction: parallaxPx / renderWidth,
    j995,
    jmax,
    tolerancePx,
  };
}

export interface DepthSanity {
  readonly min: number;
  readonly max: number;
  readonly stdDev: number;
  /** Mean normalised depth of the central third, minus that of the border. */
  readonly centreMinusBorder: number;
}

/**
 * Is this depth field a depth field at all?
 *
 * A quantised model on an execution provider that does not implement one of its
 * operators does not throw — it returns numbers. Wrong numbers render as a
 * plausible-looking but incorrect parallax, which is the one failure this tool
 * could ship without noticing. Two open transformers.js issues (#1728, #1749)
 * document exactly that class of silent failure for q4/q8 weights on the WebGPU
 * execution provider, which is why this tool does not ship them.
 *
 * This is the cheap structural check that runs on every image: a constant field,
 * a nearly-constant field, or NaN means something is wrong with the runtime, not
 * with the photograph.
 */
export function depthSanity(field: DepthField): DepthSanity {
  const { data, width, height } = field;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  for (let i = 0; i < data.length; i += 1) {
    const v = data[i]!;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  const mean = sum / data.length;
  let acc = 0;
  for (let i = 0; i < data.length; i += 1) {
    const d = data[i]! - mean;
    acc += d * d;
  }
  const stdDev = Math.sqrt(acc / data.length);

  const x0 = Math.floor(width / 3);
  const x1 = Math.ceil((width * 2) / 3);
  const y0 = Math.floor(height / 3);
  const y1 = Math.ceil((height * 2) / 3);
  let centreSum = 0;
  let centreN = 0;
  let borderSum = 0;
  let borderN = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const v = data[y * width + x]!;
      if (x >= x0 && x < x1 && y >= y0 && y < y1) {
        centreSum += v;
        centreN += 1;
      } else {
        borderSum += v;
        borderN += 1;
      }
    }
  }
  return {
    min,
    max,
    stdDev,
    centreMinusBorder: (centreN ? centreSum / centreN : 0) - (borderN ? borderSum / borderN : 0),
  };
}

/** Below this the field carries no usable structure and the run is refused. */
export const SANITY_STDDEV_FLOOR = 0.02;

export function isDepthUsable(s: DepthSanity): boolean {
  return (
    Number.isFinite(s.min) &&
    Number.isFinite(s.max) &&
    s.max > s.min &&
    s.stdDev >= SANITY_STDDEV_FLOOR
  );
}
